/**
 * Fretboard grader — set-membership scoring for tap-to-answer guitar
 * exercises. Peer to `gradeDictation`; Practice dispatches to it based on
 * `exercise.exerciseType`.
 *
 * The answer is a *set* of accepted positions (the one-to-many fret layout,
 * §4). A tap is `correct` if it's a member contributing a new required pitch,
 * `duplicate` if its pitch class was already placed, `wrong` otherwise. For
 * inversion items, the lowest-sounding tap must also carry the bass pitch
 * class — the bass note *is* the inversion (§6.2).
 *
 * Pure: same inputs → same output, no I/O. Independent of
 * `@music-app/learning-system` (the `LocalRating` union mirrors that
 * package's `Rating` without importing it). The exposed `accuracy`/`bassError`
 * are for UI feedback only — the session runner rates these on the standard
 * recognition correctness+time path, not an accuracy map (§6.3).
 *
 * See docs/design/guitar-harmony-v1.md §6.2–6.4.
 */

import type { Exercise } from "./exercise.js";
import {
  STANDARD_TUNING,
  midiAt,
  pitchClassAt,
  type FretPosition,
  type PitchClass,
} from "@music-app/theory-engine";

export type FretTapStatus =
  | "correct" // ∈ acceptedPositions AND contributes a new required pitch
  | "duplicate" // ∈ acceptedPositions but its pitch class was already placed
  | "wrong"; // ∉ acceptedPositions (wrong note, or outside allowedStrings)

export interface FretboardGrading {
  /** Every required pitch placed exactly once, no wrong/duplicate taps, and
   *  (if bassPitchClass is set) the lowest tap carries the bass pitch class. */
  correct: boolean;
  /** One status per tap, in tap order. Length = userTaps.length. */
  perTap: FretTapStatus[];
  /** distinctRequiredPitchesPlaced / notesToPlace. 0..1. UI feedback only. */
  accuracy: number;
  /** True when all required pitches are present but the bass constraint failed. */
  bassError: boolean;
  /** One-sentence post-answer explanation. */
  explanation: string;
}

/**
 * Local copy of FSRS's `Rating` string union — keeps exercise-generator
 * independent of `@music-app/learning-system` at the package-graph level,
 * exactly as `gradeDictation` does. The fretboard exercises rate on the
 * recognition correctness+time path, so this is not consumed here; it exists
 * for parity and for callers that want the type re-exported from one place.
 */
type LocalRating = "again" | "hard" | "good" | "easy";
export type FretboardRating = LocalRating;

const samePosition = (a: FretPosition, b: FretPosition): boolean =>
  a.stringIndex === b.stringIndex && a.fret === b.fret;

export function gradeFretboard(
  exercise: Exercise,
  userTaps: FretPosition[],
): FretboardGrading {
  const prompt = exercise.meta.fretboard;
  if (!prompt) {
    // Defensive — a fretboard exercise with no prompt is a generator bug;
    // treat any submission as correct rather than gaslighting the user
    // (mirrors gradeDictation's empty-sequence guard).
    return {
      correct: true,
      perTap: userTaps.map(() => "correct"),
      accuracy: 1,
      bassError: false,
      explanation: "Nothing to grade.",
    };
  }

  const tuning = STANDARD_TUNING; // prompt.tuning === "standard" in v1
  const accepted = prompt.acceptedPositions;
  const placedPitchClasses = new Set<PitchClass>();
  const perTap: FretTapStatus[] = [];

  for (const tap of userTaps) {
    const isMember = accepted.some((p) => samePosition(p, tap));
    if (!isMember) {
      perTap.push("wrong");
      continue;
    }
    const pc = pitchClassAt(tuning, tap);
    if (placedPitchClasses.has(pc)) {
      perTap.push("duplicate");
      continue;
    }
    placedPitchClasses.add(pc);
    perTap.push("correct");
  }

  const distinctCorrect = placedPitchClasses.size;
  const accuracy = prompt.notesToPlace === 0 ? 1 : distinctCorrect / prompt.notesToPlace;

  let bassError = false;
  if (distinctCorrect === prompt.notesToPlace && prompt.bassPitchClass !== undefined && userTaps.length > 0) {
    const lowest = userTaps.reduce((lo, t) =>
      midiAt(tuning, t) < midiAt(tuning, lo) ? t : lo,
    );
    bassError = pitchClassAt(tuning, lowest) !== prompt.bassPitchClass;
  }

  const correct =
    userTaps.length === prompt.notesToPlace &&
    perTap.every((s) => s === "correct") &&
    !bassError;

  return {
    correct,
    perTap,
    accuracy,
    bassError,
    explanation: buildFretboardExplanation(perTap, prompt.notesToPlace, distinctCorrect, bassError, correct),
  };
}

function buildFretboardExplanation(
  perTap: FretTapStatus[],
  notesToPlace: number,
  distinctCorrect: number,
  bassError: boolean,
  correct: boolean,
): string {
  if (correct) return notesToPlace === 1 ? "Correct." : "Right shape.";
  if (bassError) return "Right notes — wrong bass note.";
  if (perTap.length === 0) return "Nothing placed yet.";
  const wrong = perTap.filter((s) => s === "wrong").length;
  if (notesToPlace === 1) {
    return wrong > 0 ? "Not that note — that's a different pitch." : "Place the upper note.";
  }
  return `${distinctCorrect} of ${notesToPlace} placed.`;
}
